/**
 * Config backup decryption — WebCrypto only, no dependencies, runs in the
 * browser (and in tests via Node's global webcrypto).
 *
 * PBKDF2-SHA256 key derivation + AES-256-GCM open. A wrong passphrase and
 * tampered ciphertext both fail the GCM auth tag and surface as
 * `ConfigBackupPassphraseError` — indistinguishable by design.
 */
import {
  type BackupEnvelope,
  ConfigBackupPassphraseError,
} from './bundle.js';

const KEY_BITS = 256;
const IV_BYTES = 12;
const AUTH_TAG_BYTES = 16;

/** base64 → bytes with a canonical round-trip check (hostile-input safe). */
function decodeBase64(value: string, field: string): Uint8Array {
  const trimmed = value.trim();
  const binary = atob(trimmed);
  // Canonical re-encode must match — Buffer.from/atob are lenient with junk.
  if (btoa(binary) !== trimmed) {
    throw new ConfigBackupPassphraseError(
      `Backup envelope field "${field}" is corrupt`,
    );
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Opens a sealed envelope and returns the UTF-8 payload JSON string.
 * Throws `ConfigBackupPassphraseError` on a wrong passphrase, tampered
 * data, or an envelope this build does not understand.
 */
export async function decryptBackupPayload(
  envelope: BackupEnvelope,
  passphrase: string,
): Promise<string> {
  if (!passphrase) throw new ConfigBackupPassphraseError('Passphrase is empty');
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('WebCrypto is unavailable (requires HTTPS or localhost).');
  }

  const salt = decodeBase64(envelope.salt, 'salt');
  const iv = decodeBase64(envelope.iv, 'iv');
  const authTag = decodeBase64(envelope.authTag, 'authTag');
  const ciphertext = decodeBase64(envelope.data, 'data');
  if (iv.length !== IV_BYTES) {
    throw new ConfigBackupPassphraseError('Backup envelope iv is corrupt');
  }
  if (authTag.length !== AUTH_TAG_BYTES) {
    throw new ConfigBackupPassphraseError('Backup envelope auth tag is corrupt');
  }

  const passphraseBytes = new TextEncoder().encode(passphrase);
  const keyMaterial = await subtle.importKey(
    'raw',
    passphraseBytes,
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const keyBits = await subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: salt as unknown as BufferSource,
      iterations: envelope.pbkdf2.iterations,
    },
    keyMaterial,
    KEY_BITS,
  );
  const key = await subtle.importKey('raw', keyBits, 'AES-GCM', false, [
    'decrypt',
  ]);

  // WebCrypto expects ciphertext||tag as the sealed body.
  const sealed = new Uint8Array(ciphertext.length + authTag.length);
  sealed.set(ciphertext, 0);
  sealed.set(authTag, ciphertext.length);

  try {
    const plain = await subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource, tagLength: 128 },
      key,
      sealed as unknown as BufferSource,
    );
    return new TextDecoder().decode(plain);
  } catch {
    throw new ConfigBackupPassphraseError();
  }
}
